export type OperatorId = 'mpt' | 'atom' | 'u9' | 'mytel';
export type PackageStatus = 'open' | 'closed_daily_limit' | 'closed_other';
export type ServiceStatus = 'open' | 'closed';

export interface Operator {
  id: OperatorId;
  name: string;
  package_status: PackageStatus;
  bill_sell_status: ServiceStatus;
  bill_buy_status: ServiceStatus;
  status_reason: string | null;
  reopen_at: string | null;
  activePackagesCount?: number;
  pendingOrdersCount?: number;
}

export interface PackageItem {
  id: string;
  operator_id: OperatorId;
  name: string;
  data_allowance: string;
  validity_days: number;
  eligibility: string | null;
  cost_price: number;
  selling_price: number;
  is_active: number;
}

export interface AirtimeDenomination {
  id: string;
  operator_id: OperatorId;
  airtime_amount: number;
  selling_price: number;
  cost_price: number;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

export interface Wallet {
  user_id?: string;
  balance: number;
  reserved_balance?: number;
  reservedBalance?: number;
  availableBalance?: number;
}

export function getAvailableBalance(wallet?: Wallet | null): number {
  if (!wallet) return 0;
  if (typeof wallet.availableBalance === 'number' && !isNaN(wallet.availableBalance)) {
    return Math.max(0, wallet.availableBalance);
  }
  const balance = Number(wallet.balance) || 0;
  const reserved = Number(wallet.reserved_balance ?? wallet.reservedBalance) || 0;
  return Math.max(0, balance - reserved);
}

export function getReservedBalance(wallet?: Wallet | null): number {
  if (!wallet) return 0;
  return Math.max(0, Number(wallet.reserved_balance ?? wallet.reservedBalance) || 0);
}

export function getTotalBalance(wallet?: Wallet | null): number {
  if (!wallet) return 0;
  return Math.max(0, Number(wallet.balance) || 0);
}

export interface Order {
  id: string;
  user_id: string;
  operator_id: OperatorId;
  package_id: string | null;
  order_type: 'package' | 'phone_bill';
  recipient_phone: string;
  amount: number;
  airtime_amount?: number | null;
  cost_amount?: number;
  payment_method: 'wallet' | 'direct_kpay' | 'direct_wave';
  payment_status: 'pending' | 'under_review' | 'verified' | 'rejected';
  fulfillment_status: 'queued' | 'processing' | 'completed' | 'unable_to_deliver' | 'outcome_uncertain';
  direct_tx_ref?: string;
  direct_slip_url?: string;
  claimed_by?: string;
  delivery_ref?: string;
  delivery_note?: string;
  created_at: string;
}

export interface Deposit {
  id: string;
  user_id: string;
  amount: number;
  payment_method: 'kpay' | 'wavepay' | 'cbpay' | 'kbzpay';
  tx_ref?: string;
  slip_url?: string;
  status: 'pending' | 'under_review' | 'credited' | 'rejected';
  rejection_reason?: string;
  created_at: string;
}

export interface Buyback {
  id: string;
  user_id: string;
  operator_id: OperatorId;
  airtime_amount: number;
  rate_percentage: number;
  payout_amount: number;
  settlement_type: 'wallet' | 'external_kpay' | 'external_wave';
  sender_phone: string;
  payout_destination?: string;
  status: 'quoted' | 'awaiting_transfer' | 'under_review' | 'receipt_verified' | 'settled' | 'rejected';
  transfer_proof_url?: string;
  payout_tx_ref?: string;
  created_at: string;
}

export interface ReceivingAccount {
  id: string;
  type: 'payment' | 'airtime';
  provider: string;
  account_name: string;
  account_number: string;
  instructions: string | null;
}

export interface RateSchedule {
  id: string;
  operator_id: OperatorId;
  direction: 'buy' | 'sell';
  rate_percentage: number;
  min_amount?: number;
  max_amount?: number;
  transfer_fee?: number;
  is_active?: number;
}

export interface DashboardSummary {
  totalRevenue: number;
  totalProfit: number;
  totalCost: number;
  lifetimeRevenue: number;
  lifetimeProfit: number;
  profitMargin: number;
  todayRevenue: number;
  todayProfit: number;
  thisMonthRevenue: number;
  thisMonthProfit: number;
  completedOrdersCount: number;
  pendingOrdersCount: number;
  cancelledOrdersCount: number;
  averageOrderValue: number;
  totalDepositsCredited: number;
  totalAirtimeBuybackSettled: number;
  buybackSpreadProfit: number;
}

export interface DashboardChartPoint {
  date: string;
  revenue: number;
  profit: number;
  cost: number;
  ordersCount: number;
}

export interface OperatorStat {
  operatorId: OperatorId;
  name: string;
  revenue: number;
  profit: number;
  ordersCount: number;
  percentage: number;
}

export interface OrderTypeStat {
  orderType: 'package' | 'phone_bill';
  revenue: number;
  profit: number;
  ordersCount: number;
}

export interface TopPackageStat {
  id: string;
  name: string;
  operatorId: OperatorId;
  dataAllowance: string;
  ordersCount: number;
  revenue: number;
  profit: number;
}

export type DashboardTimeframe = number | 'all' | 'today' | 'month' | 'custom';

export interface DashboardDateRange {
  startDate: string;
  endDate: string;
  label: string;
}

export interface DashboardFilter {
  days?: DashboardTimeframe;
  startDate?: string;
  endDate?: string;
}

export interface DashboardStats {
  summary: DashboardSummary;
  chartData: DashboardChartPoint[];
  operatorStats: OperatorStat[];
  orderTypeStats: OrderTypeStat[];
  topPackages: TopPackageStat[];
  timeframeDays: DashboardTimeframe;
  dateRange?: DashboardDateRange;
}

export interface AdminCustomerItem {
  id: string;
  telegram_id: string | null;
  username: string | null;
  role: 'customer' | 'admin' | 'superadmin';
  name: string;
  phone: string | null;
  is_banned?: number;
  ban_reason?: string | null;
  banned_at?: string | null;
  banned_by?: string | null;
  created_at: string;
  wallet_balance: number;
  wallet_reserved: number;
  completed_orders_count: number;
  total_spent: number;
  total_orders_count: number;
  last_order_at: string | null;
}

export interface AdminCustomerSummary {
  totalCustomersCount: number;
  activeCustomersCount: number;
  bannedCustomersCount?: number;
  totalWalletBalanceLiability: number;
  totalCustomerSpend: number;
}

export interface AdminCustomerListResult {
  customers: AdminCustomerItem[];
  summary: AdminCustomerSummary;
}

export interface AdminCustomerDetailResult {
  customer: AdminCustomerItem;
  orders: Order[];
  deposits: Deposit[];
}

